import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/vue';

import { CommonProperties, services } from '@voicemod1/event-tracking-helper';

import {
	clearAcquisitionValues,
	getAcquisitionValues,
	saveAcquisitionValues,
} from './acquisition';
import { MetricsError } from './types';

import { abTests } from '@/experiments';
import { getCurrentLocale } from '@/i18n/i18n-setup';
import { CookieService } from '@/infrastructure/services/cookie';

type UserIdentities = {
	customerid?: string;
	email?: string;
	other?: string;
};

type UserAttributeValue =
	| string
	| number
	| boolean
	| string[]
	| null
	| undefined;

type EventProperties = Record<string, unknown>;

const AB_TEST_ATTR_NAME = 'Accounts_Web_AB_Test_Split';

const getAppVersion = (): string | undefined =>
	import.meta.env.VUE_APP_TAG_NAME || import.meta.env.VUE_APP_VERSION;

// Route Amplitude ingestion through a dedicated first-party proxy domain
// (amp.voicemod.tech/api2 -> api2.amplitude.com). The default endpoint lives on the
// third-party host api2.amplitude.com, which ad-blockers / tracking-protection block at the
// network layer, silently dropping a large share of browser-sent events. The proxy domain is
// not on those blocklists, so events get through.
const AMPLITUDE_SERVER_URL = 'https://amp.voicemod.tech/api2/2/httpapi';

// Read-only capture of the legacy mParticle cookie (mprtcl-v4_*). mParticle is no longer
// initialized, so we never create, refresh or delete this cookie: we only read the pre-existing
// ones (which persist for up to a year) to reuse their device id (gs.das) as the Amplitude
// deviceId, preserving device continuity. If the cookie is absent we simply don't set a deviceId
// (Amplitude generates its own); events are always sent regardless.
const getDeviceIdFromMparticleCookie = (): string | undefined => {
	if (typeof document === 'undefined') {
		return undefined;
	}

	const cookieEntry = document.cookie
		.split('; ')
		.find((entry) => entry.startsWith('mprtcl-v4_'));

	if (!cookieEntry) {
		return undefined;
	}

	const rawValue = cookieEntry.substring(cookieEntry.indexOf('=') + 1);

	try {
		const decoded = rawValue.replace(/\|/g, ',').replace(/'/g, '"');
		const persistence = JSON.parse(decodeURIComponent(decoded));

		return persistence?.gs?.das;
	} catch (error) {
		Sentry.addBreadcrumb({
			type: 'amplitude',
			message: `Failed to parse mParticle cookie for deviceId: ${
				(error as Error).message
			}`,
			level: 'warning',
		});

		return undefined;
	}
};

// Acquisition properties (UTMs, gclid, fbclid) aren't set here: this runs on every page load and login, and they
// must only be written once, at sign-up (see setAcquisitionUserProperties)
const applyAmplitudeIdentitySideEffects = (): void => {
	const identify = new Identify();
	identify.set('locale', getCurrentLocale());

	const experiments = abTests.getFormattedActiveExperiments();
	if (experiments.length > 0) {
		identify.set(AB_TEST_ATTR_NAME, experiments);
	}

	amplitude.identify(identify);
};

const initAmplitude = (cookieDomain: string): void => {
	const { VUE_APP_AMPLITUDE_KEY } = import.meta.env;

	if (!VUE_APP_AMPLITUDE_KEY) {
		console.warn(
			'[Amplitude] VUE_APP_AMPLITUDE_KEY is not set. Skipping init.'
		);

		return;
	}

	amplitude
		.init(VUE_APP_AMPLITUDE_KEY, {
			defaultTracking: false,
			deviceId: getDeviceIdFromMparticleCookie(),
			appVersion: getAppVersion(),
			cookieOptions: { domain: cookieDomain },
			serverUrl: AMPLITUDE_SERVER_URL,
		})
		.promise.then(applyAmplitudeIdentitySideEffects)
		.catch((error) => {
			MetricsError.report({
				cause: error,
				description: 'Failed to init Amplitude',
			});
		});
};

const sendPendingEvents = (): void => {
	amplitude.flush();
};

let isFlushOnHideRegistered = false;

// Amplitude batches events in memory and flushes them via `fetch`, which the browser
// aborts when the page is torn down (e.g. a hard `window.location` navigation to a
// download URL). Flushing on `pagehide`/`visibilitychange` forces the pending Amplitude
// batch out before the page goes away.
const registerFlushOnHide = (): void => {
	if (isFlushOnHideRegistered || typeof window === 'undefined') {
		return;
	}
	isFlushOnHideRegistered = true;

	window.addEventListener('pagehide', () => sendPendingEvents());
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			sendPendingEvents();
		}
	});
};

const init = (): void => {
	const isDevelopmentMode =
		import.meta.env.VUE_APP_ENVIRONMENT === 'development';
	const cookieDomain = isDevelopmentMode ? 'voicemod.dev' : 'voicemod.net';

	saveAcquisitionValues();
	initAmplitude(cookieDomain);
	registerFlushOnHide();
};

const login = (data: UserIdentities): void => {
	if (data.customerid) {
		amplitude.setUserId(data.customerid);
	}

	applyAmplitudeIdentitySideEffects();
};

/**
 * Saves the UTMs and ad click IDs that brought the user in, so the app's sign-in and Pro conversion events (same
 * user_id) can be tied back to the channel. Call it only for sign-ups, after login(): setOnce keeps the values
 * voicemod.net already saved on this device (first touch wins), and logins must not add or change any.
 */
const setAcquisitionUserProperties = (): void => {
	const values = getAcquisitionValues();
	clearAcquisitionValues();

	const entries = Object.entries(values);
	if (entries.length === 0) {
		return;
	}

	const identify = new Identify();
	entries.forEach(([property, value]) => {
		identify.setOnce(property, value);
	});
	amplitude.identify(identify);
};

const track = async (
	event: string,
	params: EventProperties = {}
): Promise<void> => {
	try {
		const commonProperties = new CommonProperties({
			serviceName: services.ACCOUNTVOICEMODNET,
		});
		await commonProperties.enableCountryCode();

		const downloadId = CookieService.getCookie('download_id');
		const accountsWebVersion = getAppVersion();

		const enrichedProperties = {
			...commonProperties.toJSON(),
			...params,
			download_id: downloadId,
			accounts_web_version: accountsWebVersion,
		};

		amplitude.track(event, enrichedProperties);
	} catch (error) {
		const err = error as Error;
		Sentry.addBreadcrumb({
			type: 'amplitude',
			message: `Tracking event failed: ${err.message}.`,
			level: 'error',
		});
		MetricsError.report({
			cause: err,
			description: 'Tracking event failed',
			context: {
				event,
				params,
			},
		});
	}
};

const setUserAttribute = (
	attribute: string,
	value: UserAttributeValue
): void => {
	const identify = new Identify();
	identify.set(attribute, value as string | number | boolean | string[]);
	amplitude.identify(identify);
};

const appendToUserAttribute = (
	attribute: string,
	value: UserAttributeValue
): void => {
	const identify = new Identify();
	identify.postInsert(attribute, value as string | number | boolean | string[]);
	amplitude.identify(identify);
};

const mParticleMetric = {
	init,
	login,
	setAcquisitionUserProperties,
	track,
	setUserAttribute,
	appendToUserAttribute,
	sendPendingEvents,
};

export { getDeviceIdFromMparticleCookie, mParticleMetric };
